<template>
  <div class="container">
    <div class="chat-container">
      <div class="chat-header">
        <h2>Group Chat</h2>
      </div>
      <div class="chat-organization">
        <label for="organization-select">Organization:</label>

        <select id="organization-select" v-model="selectedOrganization" @change="fetchMessages">
          <option v-for="(org, index) in organizations" :key="index" :value="org.org_id">
            {{ org.name }}
          </option>
        </select>

      </div>
      <div class="chat-box">
        <div v-for="(message, index) in messages" :key="index"
          :class="['message-box', { 'you': message.sender_id === userId, 'system': message.sender_id === 0 }]">

          <!-- System message style -->
          <div v-if="message.sender_id === 0" class="system-message">
            <div class="message-datetime">{{ message.created_at }}</div>
            <div class="message-text">{{ message.content }}</div>
          </div>

          <!-- Regular user messages -->
          <div v-else>
            <div class="message-sender">{{ getUserName(message.sender_id) }}:</div>
            <div class="message-datetime">{{ message.created_at }}</div>
            <div class="message-text">{{ message.content }}</div>
          </div>
        </div>
      </div>
      <div class="chat-input">
        <input v-model="newMessageData.content" placeholder="Type your message..." />
        <button @click="sendMessage">Send</button>
      </div>
    </div>
  </div>
</template>

<script>
import apiService from '@/services/apiService';
import { nextTick } from 'vue';

export default {
  data() {
    return {
      messages: [],
      newMessageData: { content: '', chat_id: null },
      selectedOrganization: '',
      organizations: [],
      organizationUsers: [],
      userId: null, // Add userId to store the logged-in user's ID
    };
  },

  created() {
    // Get userId from localStorage when component is created
    this.userId = parseInt(localStorage.getItem('userId'));
  },

  mounted() {
    this.fetchOrganizations();
    this.fetchAllUsers();
  },

  methods: {
    scrollToBottom() {
      const chatBox = this.$el.querySelector('.chat-box');
      chatBox.scrollTop = chatBox.scrollHeight;
    },

    async fetchAllUsers() {
      try {
        console.log("Fetching users...");
        const users = await apiService.fetchUsers();
        console.log("Users loaded:", users);
        this.organizationUsers = users ? users : [];
      } catch (error) {
        console.error("Error fetching users:", error);
        this.organizationUsers = [];
      }
    },

    async fetchOrganizations() {
      try {
        this.organizations = await apiService.fetchOrganizations();
        console.log("Organizations loaded:", this.organizations);
      } catch (error) {
        console.error("Error fetching organizations:", error);
      }
    },

    async fetchMessages() {
      if (!this.selectedOrganization) return;

      try {
        const messages = await apiService.fetchChatMessages(this.selectedOrganization);

        this.messages = messages.map(message => ({
          ...message,
          sender_name: message.sender_name || 'Unknown',
          recipient_name: message.recipient_name || 'Unknown'
        }));

        await nextTick();
        this.scrollToBottom();
      } catch (error) {
        console.error("Error fetching messages:", error);
      }
    },

    async sendMessage() {
      if (!this.newMessageData.content.trim()) return;

      try {
        await apiService.sendChatMessage(this.selectedOrganization, {
          content: this.newMessageData.content,
          sender_id: this.userId
        });

        await this.fetchMessages();
        this.newMessageData.content = '';
        await nextTick();
        this.scrollToBottom();
      } catch (error) {
        console.error("Error sending message:", error);
      }
    },

    getUserName(sender_id) {
      // Compare with the stored userId instead of parsing cookie
      if (sender_id === this.userId) {
        return "You";
      }

      const user = this.organizationUsers.find(user => user.id === sender_id);
      return user ? user.username : "Unknown";
    }
  }
};
</script>

<style scoped>
.container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  padding: 20px;
}

.chat-container {
  width: 100%;
  max-width: 600px;
  height: 700px;
  display: flex;
  flex-direction: column;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #f9f9f9;
  margin: 0 auto;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  font-family: 'Montserrat', 'Avenir', Helvetica, Arial, sans-serif;
}

.chat-header {
  background-color: #4e9e81;
  color: white;
  padding: 15px;
  text-align: center;
  font-size: 1.3rem;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}

.chat-organization {
  padding: 12px 15px;
  background-color: #f5f5f5;
  border-bottom: 1px solid #e8e9f5;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

label {
  font-weight: 500;
  color: #505050;
  margin-right: 10px;
}

select {
  padding: 8px 10px;
  border: 1px solid #e8e9f5;
  border-radius: 4px;
  width: 100%;
  max-width: 300px;
  background-color: white;
  color: #404040;
  font-size: 14px;
}

.chat-box {
  flex-grow: 1;
  padding: 15px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  background-color: white;
  border-bottom: 1px solid #e8e9f5;
}

/* Style for the individual message box */
.message-box {
  background-color: #f0f0f0;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 12px;
  width: fit-content;
  max-width: 80%;
  word-wrap: break-word;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

/* Different style for messages sent by the current user */
.message-box.you {
  background-color: #edf7f3;
  align-self: flex-end;
  margin-left: auto;
  border-left: 3px solid #4e9e81;
}

/* Special style for system notifications */
.message-box.system {
  background-color: #f7f7f7;
  align-self: center;
  max-width: 80%;
  text-align: center;
  color: #606060;
  border: 1px dashed #d0d0d0;
  font-style: italic;
}

.message-sender {
  font-weight: 600;
  color: #4e9e81;
  margin-bottom: 2px;
}

.message-datetime {
  font-size: 0.75rem;
  color: #888;
  margin-top: 2px;
  margin-bottom: 4px;
}

.message-text {
  margin-top: 5px;
  line-height: 1.4;
  color: #404040;
}

.chat-input {
  display: flex;
  padding: 12px 15px;
  border-top: 1px solid #e8e9f5;
  background-color: #fff;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
}

input {
  flex-grow: 1;
  padding: 10px 12px;
  border: 1px solid #e8e9f5;
  border-radius: 4px;
  margin-right: 10px;
  font-size: 14px;
  color: #404040;
}

input:focus {
  outline: none;
  border-color: #4e9e81;
}

button {
  background-color: #4e9e81;
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  transition: background-color 0.2s ease;
}

button:hover {
  background-color: #3e8d70;
}

/* Responsive design */
@media (max-width: 768px) {
  .chat-container {
    width: 90%;
    max-width: 100%;
  }
}

@media (max-width: 480px) {
  .chat-container {
    height: 500px;
  }

  .chat-header h2 {
    font-size: 1.2rem;
  }

  button {
    padding: 8px 12px;
  }
}
</style>